import React from 'react'
import { graphql } from 'gatsby'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import Seo from '~/components/core/seo'
import { Layout } from '~/components/core/Layout'
import PageLayout from '~/components/layouts/page'
import pageAlternatesUrls from '~/utils/pageAlternatesUrls'
import GetNewsroomUrls from '~/utils/getNewsroomUrls'
import { Helmet } from 'react-helmet'

function Page({ data, pageContext }) {
  const mainContent = data.mainContent
  const newsroomUrls = GetNewsroomUrls()
  let urls = null

  if (mainContent.customType === 'Blog') {
    urls = pageAlternatesUrls(
      mainContent,
      data.alternateContent,
      'title',
      `/${newsroomUrls['Blog']['fr']}/`,
      `/${newsroomUrls['Blog']['en']}/`,
      pageContext.locale,
    )
  } else if (mainContent.customType === 'Médiathèque') {
    urls = pageAlternatesUrls(
      mainContent,
      data.alternateContent,
      'title',
      `/${newsroomUrls['Médiathèque']['fr']}/`,
      `/${newsroomUrls['Médiathèque']['en']}/`,
      pageContext.locale,
    )
  } else {
    urls = pageAlternatesUrls(
      mainContent,
      data.alternateContent,
      'slug',
      null,
      null,
      pageContext.locale,
    )
  }

  return (
    <Layout
      frUrl={urls.frUrl}
      enUrl={urls.enUrl}
      initLocale={pageContext.locale}
      profile={
        mainContent.profile !== undefined && mainContent.profile !== null
          ? mainContent.profile.title
          : null
      }
      mainTag={mainContent.mainTag.title}
      noBg={mainContent.mainImage ? false : true}
    >
      <Seo
        frUrl={urls.frUrl}
        enUrl={urls.enUrl}
        page={{
          title: mainContent.seoTitle
            ? mainContent.seoTitle
            : mainContent.title,
          description: mainContent.seoDescription
            ? mainContent.seoDescription.seoDescription
            : documentToPlainTextString(mainContent?.chapo?.raw),
          image: mainContent?.mainImage?.file?.file?.url ?? null,
          path: pageContext.locale === 'fr' ? urls.frUrl : urls.enUrl,
        }}
      />

      {mainContent.customType === 'Blog' && (
        <Helmet>
          <script type="application/ld+json">
            {`{
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": "${mainContent.seoTitle ? mainContent.seoTitle : mainContent.title}",
            "datePublished": "${mainContent.createdAt}",
            "dateModified": "${mainContent.updatedAt}"
            ${
              mainContent.mainImage?.file?.file?.url
                ? `,
              "image": [
                "${mainContent.mainImage.file.file.url}"
              ]`
                : ''
            }
          }`}
          </script>
        </Helmet>
      )}

      <PageLayout
        currentUrl={pageContext.locale === 'fr' ? urls.frUrl : urls.enUrl}
        data={mainContent}
        furtherContent={data.furtherContent}
      />
    </Layout>
  )
}

export const pageQuery = graphql`
  query SinglePage(
    $contentful_id: String!
    $locale: String!
    $mainTag: String!
    $customType: String!
  ) {
    mainContent: contentfulPage(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      customType
      internal {
        type
      }
      updatedAt
      createdAt
      seoTitle
      seoDescription {
        seoDescription
      }
      mainImage {
        altImage
        file {
          file {
            url
          }
        }
      }
      mainTag {
        title
      }
      profile: profileBis {
        title
      }
      breadcrumb {
        pageLink {
          ... on Node {
            ... on ContentfulDatasOdre {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulEvent {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulNews {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulProject {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulLabo {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHomepage {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulContact {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulGovernance {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSimulator {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulProfile {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulAction {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHub {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulPage {
              title
              slug
              customType
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulMap {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulNewsroomList {
              title
              slug
              node_locale
              internal {
                type
              }
            }
          }
        }
        label
        customLink
        assetLink {
          file {
            url
          }
        }
      }
      breadcrumbCurrentLabel
      title
      slug
      chapo {
        raw
        references {
          ...ContentfulFullReferenceFields
        }
      }
      chapoCta {
          pageLink {
            ... on Node {
              ... on ContentfulDatasOdre {
                title
                slug
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulEvent {
                title
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulNews {
                title
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulProject {
                title
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulLabo {
                title
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulHomepage {
                title
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulContact {
                title
                slug
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulGovernance {
                title
                slug
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulSimulator {
                title
                slug
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulProfile {
                title
                slug
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulAction {
                title
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulHub {
                title
                slug
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulPage {
                title
                slug
                customType
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulMap {
                title
                slug
                node_locale
                internal {
                  type
                }
              }
              ... on ContentfulNewsroomList {
                title
                slug
                node_locale
                internal {
                  type
                }
              }
            }
          }
          label
          customLink
          assetLink {
            file {
              url
            }
          }
        }
      node_locale
      slices {
        ... on Node {
          ...SliceSimulatorFuelData
          ... on ContentfulSliceGroupeDaction {
            internal {
              type
            }
            title
            actionsInformations {
              title
              parentPage {
                slug
              }
              createdAt
              internal {
                type
              }
              profile {
                title
              }
              node_locale
              mainTag {
                title
              }
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
              date
            }
          }

          ... on ContentfulFullText {
            internal {
              type
            }
            displayTitle
            title
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulDatasOdre {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulProject {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLabo {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepage {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulGovernance {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulProfile {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulAction {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulMap {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              label
              customLink
              assetLink {
                file {
                  url
                }
              }
            }
            content {
              raw
              references {
                ...ContentfulFullReferenceFields
              }
            }
            node_locale
          }
          ... on ContentfulSliceImage {
            internal {
              type
            }
            node_locale
            titleContent
            link
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulSlicePdf {
            internal {
              type
            }
            node_locale
            iframe
          }
          ... on ContentfulImageTexteSection {
            internal {
              type
            }
            node_locale
            title
            content {
              raw
              references {
                ...ContentfulFullReferenceFields
              }
            }
            proportions
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulDatasOdre {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulProject {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLabo {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepage {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulGovernance {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulProfile {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulAction {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulMap {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              customLink
              label
              assetLink {
                file {
                  url
                }
              }
            }
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
            reversed
            theme
          }
          ... on ContentfulTabsaccordion {
            internal {
              type
            }
            type
            titleContent
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulDatasOdre {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulProject {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLabo {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepage {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulGovernance {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulProfile {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulAction {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulMap {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              label
              customLink
              assetLink {
                file {
                  url
                }
              }
            }
            items {
              title
              subtitle1
              subtitle2
              content {
                raw
                references {
                  ...ContentfulFullReferenceFields
                }
              }
              cta {
                pageLink {
                  ... on Node {
                    ... on ContentfulDatasOdre {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulEvent {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulNews {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulProject {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulLabo {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulHomepage {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulContact {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulGovernance {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulSimulator {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulProfile {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulAction {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulHub {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulPage {
                      title
                      slug
                      customType
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulMap {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulNewsroomList {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                  }
                }
                label
                customLink
                assetLink {
                  file {
                    url
                  }
                }
              }
              contactItems {
                title
                icon
                link {
                  pageLink {
                    ... on Node {
                      ... on ContentfulDatasOdre {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulEvent {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulNews {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulProject {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulLabo {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulHomepage {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulContact {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulSimulator {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulGovernance {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulProfile {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulAction {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulHub {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulPage {
                        title
                        slug
                        customType
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulMap {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulNewsroomList {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                    }
                  }
                  customLink
                  label
                  assetLink {
                    file {
                      url
                    }
                  }
                }
                content {
                  raw
                }
              }
            }
          }
          ... on ContentfulVideo {
            internal {
              type
            }
            title
            suptitle
            youtubeId
            youtubePlaylistId
            cover {
              file {
                url
              }
            }
          }
          ... on ContentfulTestimony {
            internal {
              type
            }
            author
            job
            theme
            content {
              content
            }
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulSliderPartnersClients {
            internal {
              type
            }
            title
            items {
              logo {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
              link
              noFollow
            }
          }
          ... on ContentfulSlider {
            internal {
              type
            }
            title
            type
            chapo {
              chapo
            }
            items {
              ... on Node {
                ... on ContentfulFrontImg {
                  altImage
                  file {
                    file {
                      url
                    }
                  }
                  legend: childContentfulFrontImgLegendTextNode {
                    legend
                  }
                }
                ... on ContentfulShortItem {
                  title
                  text {
                    text
                  }
                }
              }
            }
          }
          ... on ContentfulIframe {
            internal {
              type
            }
            title
            link
            mobileHeight
            tabletHeight
            desktopHeight
          }
        }
      }
      pushPages {
        chapo {
          chapo
        }
        ctaLabel
        page {
          ... on Node {
            ... on ContentfulDatasOdre {
              internal {
                type
              }
              title
              slug
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulSimulator {
              internal {
                type
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulNewsroomList {
              internal {
                type
              }
              title
              slug
              node_locale
            }
            ... on ContentfulEvent {
              internal {
                type
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulNews {
              internal {
                type
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulPage {
              internal {
                type
              }
              title
              slug
              customType
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulContact {
              internal {
                type
              }
              title
              slug
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulProfile {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSimulator {
              title
              slug
              node_locale
              internal {
                type
              }
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulGovernance {
              internal {
                type
              }
              title
              slug
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulAction {
              internal {
                type
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulProject {
              internal {
                type
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulLabo {
              internal {
                type
              }
              title
              node_locale
            }
            ... on ContentfulMap {
              internal {
                type
              }
              title
              slug
              node_locale
            }
            ... on ContentfulHub {
              internal {
                type
              }
              title
              slug
              node_locale
            }
            ... on ContentfulNewsroomList {
              internal {
                type
              }
              title
              slug
              node_locale
            }
          }
        }
      }
    }

    alternateContent: contentfulPage(
      contentful_id: { eq: $contentful_id }
      node_locale: { ne: $locale }
      title: { ne: null }
    ) {
      title
      slug
      node_locale
    }

    furtherContent: allContentfulPage(
      filter: {
        distributionChannel: { elemMatch: { title: { eq: "Site internet" } } }
        customType: { eq: $customType }
        contentful_id: { ne: $contentful_id }
        node_locale: { eq: $locale }
        title: { ne: null }
        mainTag: { title: { eq: $mainTag } }
      }
      limit: 3
      sort: { createdAt: DESC }
    ) {
      nodes {
        title
        slug
        internal {
          type
        }
        mainImage {
          altImage
          file {
            file {
              url
            }
          }
        }
        chapo {
          raw
          references {
            ...ContentfulFullReferenceFields
          }
        }
        node_locale
      }
    }
  }
`

export { Head } from '~/components/core/head';

export default Page
